import { type Product, type Project, projectSchema } from '@noconflict/contracts';
import { z } from 'zod';
import { apiFetch } from '../../shared/api/http';
import type { Supabase } from '../../shared/api/supabase';

/** Projects of one workspace, newest first (RLS: `projects.view` there). */
export async function listProjects(client: Supabase, workspaceId: string): Promise<Project[]> {
  const { data, error } = await client
    .from('projects')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return projectSchema.array().parse(data);
}

/**
 * Starts an order in a workspace: `start_order` creates the project (a draft), its detail row and a draft order,
 * and needs `projects.create` and `orders.create` there. Answers the new project's id.
 */
export async function startOrder(
  client: Supabase,
  workspaceId: string,
  product: Product,
): Promise<string> {
  const { data, error } = await client.rpc('start_order', {
    p_workspace_id: workspaceId,
    p_product: product,
  });
  if (error) throw new Error(error.message);
  return z.uuid().parse(data);
}

/**
 * Discards a draft for good, through the worker: it deletes what the project has stored (a script) before the
 * project, its details and its draft order. Needs `projects.edit`; refused once anything has been submitted.
 */
export async function discardDraftProject(token: string, projectId: string): Promise<void> {
  await apiFetch(`/api/projects/${projectId}/draft`, z.unknown(), { method: 'DELETE', token });
}

/** Needs `projects.move` on both workspaces, the same org, and neither workspace archived. */
export async function moveProject(
  client: Supabase,
  projectId: string,
  workspaceId: string,
): Promise<void> {
  const { error } = await client.rpc('move_project', {
    p_project_id: projectId,
    p_workspace_id: workspaceId,
  });
  if (error) throw new Error(error.message);
}
